import React from 'react';
import { } from 'react-router-dom';
import { useScrollReveal } from '../hooks/useScrollReveal';

const ServicesStatic = ({ compact = false }) => {
  // Scroll reveal hooks
  const headerReveal = useScrollReveal({ threshold: 0.2 });
  const gridReveal = useScrollReveal({ threshold: 0.1 });

  const services = [
    {
      title: 'Dog Boarding',
      tagline: 'For travel, vacations, or extended stays.',
      bullets: [
        '• Pack-based socialisation',
        '• Private temperature-controlled rooms',
        '• 24×7 human supervision',
        '• Daily photo & video updates'
      ],
    },
    {
      title: 'Dog Daycare',
      tagline: 'Because staying home alone is deeply overrated.',
      bullets: [
        '• Full-day structured play',
        '• Energy release & enrichment',
        '• Social skill development'
      ],
    },
    {
      title: 'Dog Grooming',
      tagline: 'Health first. Good looks included.',
      bullets: [
        '• Stress-free grooming',
        '• Coat care, hygiene, nail care'
      ],
    },
    {
      title: 'Dog Training',
      tagline: 'Communication, not domination.',
      bullets: [
        '• Behaviour correction',
        '• Obedience',
        '• Confidence building'
      ],
    },
    {
      title: 'Dog Swimming',
      tagline: 'Fitness disguised as fun.',
      bullets: [
        '• Low-impact exercise',
        '• Joint-friendly activity',
        '• Builds confidence & stamina'
      ],
    }
  ];

  const goToServices = (e) => {
    // Force full-page navigation to ensure the Services page loads at the top
    // (SPA navigation sometimes preserves/animates previous scroll position)
    window.location.assign('/services');
  };

  const sectionClass = compact ? 'py-8 lg:py-10' : 'py-16 lg:py-24';
  const titleClass = compact ? 'text-2xl lg:text-3xl font-bold' : 'text-3xl lg:text-4xl font-bold';
  const subtitleClass = compact ? 'text-base lg:text-lg text-gray-600 max-w-3xl mx-auto mt-2' : 'text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mt-3';
  const gridClass = compact ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8';

  return (
    <section id="services-static" className={`${sectionClass} bg-white`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={headerReveal.ref}
          className={`text-center ${compact ? 'mb-8' : 'mb-12'} transition-all duration-800 ${headerReveal.isVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8'
            }`}
        >
          <h2 className={`${titleClass} bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent`}>Our Services</h2>
          <p className={`${subtitleClass}`}>Everything your pet needs for a happy, healthy life</p>
        </div>

        <div
          ref={gridReveal.ref}
          className={`${gridClass}`}
        >
          {services.map((s, idx) => (
            <div
              key={idx}
              role="button"
              tabIndex={0}
              onClick={goToServices}
              onKeyDown={(e) => (e.key === 'Enter' ? goToServices() : null)}
              className={`block bg-gradient-to-br from-white to-gray-50 border-2 border-gray-100 rounded-2xl p-6 shadow-lg service-card-premium cursor-pointer group transition-all duration-800 ${gridReveal.isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-12'
                }`}
              style={{
                transitionDelay: gridReveal.isVisible ? `${idx * 100}ms` : '0ms'
              }}
              aria-label={`Open services page for ${s.title}`}
            >
              <div className="mb-3">
                <h3 className="text-xl font-semibold text-gray-900 group-hover:text-domigo-pink transition-colors duration-300">{s.title}</h3>
              </div>

              <div className="mb-4">
                <p className="text-gray-600 whitespace-pre-line">{s.tagline}</p>
              </div>

              <div className="mb-4 space-y-1">
                {s.bullets.map((b, i) => (
                  <p key={i} className="text-gray-700 text-sm">{b}</p>
                ))}
              </div>

              <div className="mb-6">
                <p className="text-gray-500">{s.shape}</p>
              </div>

              <div className="mt-auto">
                <button className="inline-block bg-gradient-pink text-white px-4 py-2 rounded-full font-medium transform group-hover:scale-105 transition-transform duration-300 shadow-md group-hover:shadow-lg">
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesStatic;
